import React, { useState, useEffect } from 'react';
import { object } from 'prop-types';
import { withRouter } from 'react-router-dom';
import { map, get, size, omit, head, includes, filter } from 'lodash';
import { Main } from 'base-components';
import moment from 'moment';
import { FlexContainer, Text, Loading, NotFound, HasPermissions, Icon } from '../../common';
import MediaListener from '../../utils/MediaListener';
import { Query, ModuleAccessLogger } from '../../graphql';
import GET_ORDER_WINDOWS from '../../../queries/GET_ORDER_WINDOWS';
import { arrayIsNill } from '../../../utils/misc';
import Filters from './Filters';
import Windows from './Windows';

const OrdersMonitor = ({ session, client }) => {
    const sessionSalesChannels = map(get(session, 'salesChannels', []), ({ id }) => id);
    useEffect(() => {
        setPageReload(true);
        setSelectedSalesChannels(sessionSalesChannels);
        setSelectedSalesChannelsStore(null);
        setTimeout(() => setPageReload(false), 20);
    }, [session]);

    useEffect(() => {
        setSelectedSalesChannelsStore([get(head(get(session, 'salesChannels')), 'id')]);
    }, []);

    // These hooks are used by query variables
    const [dateRange, setDateRange] = useState([moment(), moment()]);
    const [selectedSalesChannels, setSelectedSalesChannels] = useState([]);
    const [pageReload, setPageReload] = useState(false);
    const [queryKey, setQueryKey] = useState(0);

    // These hooks are used to store new values received from filters
    const [dateRangeStore, setDateRangeStore] = useState(null);
    const [selectedSalesChannelsStore, setSelectedSalesChannelsStore] = useState(null);

    const [showMsgSelectFilter, setShowMsgSelectFilter] = useState(size(sessionSalesChannels) > 1);

    const setFormFieldValue = ({ field, value }) => {
        const salesChannelId = value.value;
        switch (field) {
        case 'salesChannel':
            if (size(selectedSalesChannels) > 0) return setSelectedSalesChannelsStore([salesChannelId]);
            return setSelectedSalesChannels([salesChannelId]);
        case 'dateRange':
            return setDateRangeStore(value);
        }
    };

    const updateQueryVariables = () => {
        setShowMsgSelectFilter(!size(selectedSalesChannelsStore) === 0);
        setDateRange(dateRangeStore || dateRange);
        if (!arrayIsNill(selectedSalesChannelsStore)) setSelectedSalesChannels(selectedSalesChannelsStore || selectedSalesChannels);
        setQueryKey(prev => prev + 1);
    };

    const selectedStartDate = dateRange[0].format('YYYY-MM-DD');
    const selectedEndDate = dateRange[1].format('YYYY-MM-DD');
    const today = moment().format('YYYY-MM-DD');
    const isOnlyTodaySelected = selectedStartDate === selectedEndDate && selectedStartDate === today;

    const baseFilters = {
        salesChannel: selectedSalesChannels,
        range: {
            init: `${dateRange[0].format('YYYY-MM-DD')} 00:00:00`,
            end: isOnlyTodaySelected 
                ? `${moment().add(1, 'days').format('YYYY-MM-DD')} 23:59:59`
                : `${dateRange[1].format('YYYY-MM-DD')} 23:59:59`
        }
    };

    const initialSaleChannelSelected = () => {
        let salesChannels = get(session, 'salesChannels');

        if (size(salesChannels) === 1) {
            return {
                id: get(head(salesChannels), 'id'),
                name: `${get(head(salesChannels), 'store')} - ${get(head(salesChannels), 'name')}`
            };
        }
    };

    return (
        <HasPermissions
            to={['room-management-page:view']}
            otherwise={<NotFound firstTextLine='Página no encontrada' />}
        >
            <Main background='f4f4f4' withFooter>
                <ModuleAccessLogger module='Gestión Sala' />
                {
                    !pageReload && (
                        <MediaListener values={['16.7%', '100%', '100%']}>
                            {({ currentValue }) => {
                                const isMobile = includes(['100%'], currentValue);
                                return (
                                    <>
                                        { isMobile && (
                                            <FlexContainer pT={18} pX={23} align='center'>
                                                <Icon name='layers' size={13} right={5} color='#1EA02E' /><Text text='Gestión Sala' color='#1EA02E' size={16} />
                                            </FlexContainer>
                                        )}
                                        <Filters
                                            currentValue={currentValue}
                                            onChangeSalesChannels={({ value }) => {
                                                setFormFieldValue({ field: 'salesChannel', value });
                                            }}
                                            onChangeFilters={updateQueryVariables}
                                            initialSaleChannelSelected={initialSaleChannelSelected()}
                                            onChangeDateRange={value => setFormFieldValue({ field: 'dateRange', value })}
                                        />
                                        {
                                            showMsgSelectFilter
                                                ? <NotFound
                                                    firstTextLine='Aún no se ha realizado una búsqueda, utiliza los filtros.'
                                                    icon='find'
                                                    backgroundColor='#F4F4F4'
                                                    iconBackgroundColor='#F4F4F4'
                                                    iconColor='#c0c0c0'
                                                    iconSize={85}
                                                    fontSize={20}
                                                    centered
                                                />
                                                : <Query
                                                    key={queryKey}
                                                    query={GET_ORDER_WINDOWS}
                                                    variables={{filters: baseFilters}}
                                                    pollInterval={30000}
                                                >
                                                    {({ data, networkStatus }) => {
                                                        if (networkStatus === 1) { return <Loading loading /> }
                                                        const windows = get(data, 'getOrderWindows.payload');
                                                        const variablesWindow = {
                                                            filters: {
                                                                salesChannel: map(selectedSalesChannels, id => ({ id })),
                                                                range:  baseFilters.range,
                                                                windows: map(windows, (item) => omit(item, '__typename'))
                                                            }
                                                        };

                                                        return (<Windows
                                                            variablesWindow={variablesWindow}
                                                            isMobile={isMobile}
                                                            currentValue={currentValue}
                                                            selectedSalesChannels={selectedSalesChannels}
                                                            selectedSalesChannelsStore={selectedSalesChannelsStore}
                                                            session={session}
                                                            dateRange={dateRange}
                                                            client={client}
                                                            isOnlyTodaySelected={isOnlyTodaySelected}
                                                        />);
                                                    }}
                                                </Query>
                                        }
                                    </>
                                );
                            }}
                        </MediaListener>
                    )
                }
            </Main>
        </HasPermissions>
    );
};

OrdersMonitor.propTypes = {
    session: object
};

export default withRouter(OrdersMonitor);